import React, { useState, useEffect, useCallback } from 'react';
import api from '../../api';
import styles from '../../styles/GamesDataBrowser.module.css';

const LinesDataBrowser = () => {
  const [lines, setLines] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [filters, setFilters] = useState({
    season: '',
    week: '',
    provider: '',
    game_id: ''
  });
  const [pagination, setPagination] = useState({
    page: 1,
    limit: 50,
    total: 0,
    totalPages: 0
  });

  const fetchLines = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({
        page: pagination.page,
        limit: pagination.limit,
        ...filters
      });

      const response = await api.get(`/admin/lines?${params}`);
      setLines(response.data.lines);
      setPagination(response.data.pagination);
      setError(null);
    } catch (err) {
      setError('Failed to fetch lines data');
      console.error('Error fetching lines:', err);
    } finally {
      setLoading(false);
    }
  }, [pagination.page, pagination.limit, filters]);

  useEffect(() => {
    fetchLines();
  }, [fetchLines]);

  const handleFilterChange = (key, value) => {
    setFilters(prev => ({ ...prev, [key]: value }));
    setPagination(prev => ({ ...prev, page: 1 })); // Reset to first page
  };

  const handlePageChange = (newPage) => {
    setPagination(prev => ({ ...prev, page: newPage }));
  };

  const clearFilters = () => {
    setFilters({
      season: '',
      week: '',
      provider: '',
      game_id: ''
    });
    setPagination(prev => ({ ...prev, page: 1 }));
  };

  const formatMoneyline = (moneyline) => {
    if (!moneyline) return '-';
    return moneyline > 0 ? `+${moneyline}` : moneyline.toString();
  };

  const formatSpread = (spread) => {
    if (!spread) return '-';
    return spread > 0 ? `+${spread}` : spread.toString();
  };

  if (loading) {
    return <div className={styles.loading}>Loading lines...</div>;
  }

  if (error) {
    return <div className={styles.error}>{error}</div>;
  }

  return (
    <div className={styles.gamesDataBrowser}>
      <div className={styles.tabs}>
        <h3>Betting Lines Data Browser</h3>
      </div>

      {/* Filters */}
      <div className={styles.weekSelector}>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '10px' }}>
          <div>
            <label>Season:</label>
            <input
              type="number"
              placeholder="Season year..."
              value={filters.season}
              onChange={(e) => handleFilterChange('season', e.target.value)}
              style={{ width: '100%', padding: '8px', backgroundColor: '#3a3a3a', color: '#fff', border: '1px solid #555', borderRadius: '4px' }}
            />
          </div>
          <div>
            <label>Week:</label>
            <input
              type="number"
              placeholder="Week number..."
              value={filters.week}
              onChange={(e) => handleFilterChange('week', e.target.value)}
              style={{ width: '100%', padding: '8px', backgroundColor: '#3a3a3a', color: '#fff', border: '1px solid #555', borderRadius: '4px' }}
            />
          </div>
          <div>
            <label>Provider:</label>
            <input
              type="text"
              placeholder="Provider name..."
              value={filters.provider}
              onChange={(e) => handleFilterChange('provider', e.target.value)}
              style={{ width: '100%', padding: '8px', backgroundColor: '#3a3a3a', color: '#fff', border: '1px solid #555', borderRadius: '4px' }}
            />
          </div>
          <div>
            <label>Game ID:</label>
            <input
              type="number"
              placeholder="Game ID..."
              value={filters.game_id}
              onChange={(e) => handleFilterChange('game_id', e.target.value)}
              style={{ width: '100%', padding: '8px', backgroundColor: '#3a3a3a', color: '#fff', border: '1px solid #555', borderRadius: '4px' }}
            />
          </div>
        </div>
        <button
          onClick={clearFilters}
          style={{ marginTop: '10px', padding: '8px 16px', backgroundColor: '#007bff', color: '#fff', border: 'none', borderRadius: '4px', cursor: 'pointer' }}
        >
          Clear Filters
        </button>
      </div>

      {/* Lines Table */}
      <div className={styles.lockTable}>
        <table>
          <thead>
            <tr>
              <th>ID</th>
              <th>Game ID</th>
              <th>Provider</th>
              <th>Season</th>
              <th>Week</th>
              <th>Matchup</th>
              <th>Spread</th>
              <th>Spread Open</th>
              <th>O/U</th>
              <th>O/U Open</th>
              <th>Home ML</th>
              <th>Away ML</th>
              <th>Created</th>
            </tr>
          </thead>
          <tbody>
            {lines.map(line => (
              <tr key={line.id}>
                <td>{line.id}</td>
                <td>{line.game_id}</td>
                <td style={{ fontWeight: 'bold', color: '#007bff' }}>{line.provider}</td>
                <td>{line.season}</td>
                <td>{line.week}</td>
                <td>
                  <div style={{ fontSize: '12px' }}>
                    <div>{line.away_team}</div>
                    <div style={{ color: '#ccc' }}>@</div>
                    <div>{line.home_team}</div>
                  </div>
                </td>
                <td style={{
                  fontWeight: 'bold',
                  color: line.spread > 0 ? '#28a745' : line.spread < 0 ? '#dc3545' : '#fff'
                }}>
                  {formatSpread(line.spread)}
                </td>
                <td style={{
                  color: line.spread_open > 0 ? '#28a745' : line.spread_open < 0 ? '#dc3545' : '#ccc'
                }}>
                  {formatSpread(line.spread_open)}
                </td>
                <td style={{ fontWeight: 'bold', color: '#ffc107' }}>
                  {line.over_under || '-'}
                </td>
                <td style={{ color: '#ccc' }}>
                  {line.over_under_open || '-'}
                </td>
                <td style={{
                  fontWeight: 'bold',
                  color: line.home_moneyline > 0 ? '#28a745' : line.home_moneyline < 0 ? '#dc3545' : '#fff'
                }}>
                  {formatMoneyline(line.home_moneyline)}
                </td>
                <td style={{
                  fontWeight: 'bold',
                  color: line.away_moneyline > 0 ? '#28a745' : line.away_moneyline < 0 ? '#dc3545' : '#fff'
                }}>
                  {formatMoneyline(line.away_moneyline)}
                </td>
                <td>{new Date(line.created_at).toLocaleDateString()}</td>
              </tr>
            ))}
          </tbody>
        </table>

        {/* Pagination */}
        <div style={{ marginTop: '20px', textAlign: 'center', color: '#fff' }}>
          <span>
            Page {pagination.page} of {pagination.totalPages}
            ({pagination.total} total lines)
          </span>
          <div style={{ marginTop: '10px' }}>
            <button
              onClick={() => handlePageChange(pagination.page - 1)}
              disabled={pagination.page <= 1}
              style={{
                margin: '0 5px',
                padding: '5px 10px',
                backgroundColor: pagination.page <= 1 ? '#555' : '#007bff',
                color: '#fff',
                border: 'none',
                borderRadius: '4px',
                cursor: pagination.page <= 1 ? 'not-allowed' : 'pointer'
              }}
            >
              Previous
            </button>
            <button
              onClick={() => handlePageChange(pagination.page + 1)}
              disabled={pagination.page >= pagination.totalPages}
              style={{
                margin: '0 5px',
                padding: '5px 10px',
                backgroundColor: pagination.page >= pagination.totalPages ? '#555' : '#007bff',
                color: '#fff',
                border: 'none',
                borderRadius: '4px',
                cursor: pagination.page >= pagination.totalPages ? 'not-allowed' : 'pointer'
              }}
            >
              Next
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default LinesDataBrowser;
